/**
 * @description
 * This is one of the core Table components that represents
 * the customizable row, covers both desktop and mobile versions
 *
 */

import React, { FC, useCallback, useEffect } from 'react';
import styled from '@cthings.co/styled-components';
import { CheckBox } from '@bit/first-scope.controls';
import { Text, TextType } from '@bit/first-scope.text';
import { media } from '@cthings.co/styles-utils';
import { useTableContext, useTableFunctions } from '../../context';
import { InlineActionType, PreparedActionConfig, RowItemType, TableCustomComponentState } from '../../types';
import { ActionSection } from '../actionSection/ActionSection';
import { useLongTap } from '../../../../utils/longTap';
//images
import { ReactComponent as SelectImg } from '../../assets/select.svg';
import { ReactComponent as Delete } from '../../assets/delete.svg';
import { useStyleContext } from '@cthings.co/styles-utils';

import { colorFetchFDS as colorFetch } from '@cthings.co/styles-utils';
import { useTheme } from '@cthings.co/styled-components';
import { View } from '../../../../routes';

const StyledText = styled(Text)`
  display: block;
  word-wrap: break-word;
  & > span {
    color: ${colorFetch('gray1')};
    width: 100%;
    &.normal {
      font-weight: normal;
    }
  }
` as typeof Text;

type WrapperProps = {
  withHeaderInMobile?: boolean;
  gridTemplate?: string;
  mobileGridTemplate?: string;
  mobileGridArea?: string;
  withCheckBox: boolean;
  withHoverEffect: boolean;
};

// @TODO fix media types
const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  height: max-content;
  box-sizing: border-box;
  background-color: ${colorFetch('white')};
  display: grid;
  gap: ${({ withHeaderInMobile }) => (withHeaderInMobile ? '0' : '12px 0')};
  grid-template-columns: ${({ gridTemplate }) => gridTemplate};
  align-items: center;
  border-bottom: 1px solid;
  border-color: ${colorFetch('gray4')};
  padding: ${({ withCheckBox }) => (withCheckBox ? '12px 24px' : '12px 24px 12px 12px')};
  opacity: 1;
  transition: background 0.3s ease;
  position: static;
  z-index: auto;
  cursor: ${({ withHoverEffect }) => (withHoverEffect ? 'pointer' : 'auto')};
  &:hover {
    background-color: ${({ withHoverEffect, theme }) =>
      withHoverEffect ? colorFetch('gray6')({ theme }) : colorFetch('pureWhite')({ theme })};
  }

  ${media.tablet} {
    grid-template-columns: ${({ mobileGridTemplate }: any) => mobileGridTemplate};
    grid-template-rows: ${({ withHeaderInMobile }: any) => (withHeaderInMobile ? 'none' : 'auto')};
    grid-template-areas: ${({ mobileGridArea }: any) => mobileGridArea};
    align-items: start;
    padding: 13px 16px 13px 4px;
    &:hover {
      background-color: ${colorFetch('gray6')};
    }
  }
  &.highlighted {
    filter: drop-shadow(0px 4px 6px rgba(96, 97, 112, 0.3));
    position: relative;
    z-index: 2;
  }
  &.blurred {
    opacity: 0.5;
    user-select: none;
  }
  &.chosen {
    position: relative;
    background-color: ${colorFetch('gray6')};
    border-left: 2px solid;
    border-left-color: ${colorFetch('primary')};
    padding: 12px 24px 12px 22px;
    ${media.tablet} {
      padding: 24px 24px 24px 10px;
    }
  }
  &.rounded {
    ${media.tablet} {
      border-radius: 8px 8px 0 0;
    }
  }
`;

type GridItemProps = {
  gridArea: string;
  mobileDisplay: boolean;
  mobileAlignSelf?: string;
};

// @TODO fix media types
const GridItem = styled.div<GridItemProps>`
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  &.field {
    padding: 0 12px;
  }
  &.select {
    margin-right: 0 12px;
    align-self: center;
    ${media.tablet} {
      padding: 0px;
    }
  }
  &.actions {
    align-self: center;
  }
  ${media.tablet} {
    grid-area: ${({ gridArea }: any) => gridArea};
    display: ${({ mobileDisplay }: any) => (mobileDisplay ? 'block' : 'none')};
    align-self: ${({ mobileAlignSelf }: any) => mobileAlignSelf || 'start'};
    user-select: none;
  }
`;

type ModalProps = {
  isLast: boolean;
};

// @TODO fix media types
const Modal = styled.div<ModalProps>`
  background-color: ${colorFetch('white')};
  border-radius: ${({ theme }) => theme.borderRadius.primary};
  box-shadow: ${({ theme }) => theme.shadows.additionalShadow4};
  position: absolute;
  left: 16px;
  z-index: 5;
  width: 128px;
  display: none;
  ${media.tablet} {
    display: block;
    top: ${({ isLast }: any) => (isLast ? '-90px' : 'calc(100% + 10px)')};
  }
`;

const SelectButton = styled.button`
  height: 40px;
  background-color: transparent;
  border: none;
  outline: transparent;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid;
  border-color: ${colorFetch('gray4')};
  padding: 0 15px;
  user-select: none;
`;

const DeleteButton = styled.button`
  height: 40px;
  background-color: transparent;
  border: none;
  outline: transparent;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  user-select: none;
`;

const TextSelect = styled.p`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: ${colorFetch('black')};
  margin: 0;
`;

const DeleteText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: ${colorFetch('red2')};
  margin: 0;
`;

const SelectWrapper = styled.div`
  padding: 0;
  ${media.tablet} {
    padding: 12px;
  }
`;

export interface ManageTableRowProps {
  data: any;
  index: number;
  isFirst: boolean;
  allowDelete: boolean;
  allowSelect: boolean;
  rounded?: boolean;
  inlineActionSet: PreparedActionConfig[];
  rowRef: any;
  handleRowOnClick?: () => void;
  customRowClick?: (value: any) => void;
  idItemForViewModal?: string;
  setLastListItem?: any;
  withHeaderInMobile?: boolean;
  withHeaderGridTemplate?: string;
  languageStrings?: any;
  isHrefDetailsClick?: boolean;
}

const checkIncludesType = (typeArray: any[]) => {
  let isDetailsType = false;
  typeArray.forEach((item) => {
    if (item.type === InlineActionType.DETAILS) {
      isDetailsType = true;
    }
  });
  return isDetailsType;
};

export const Row: FC<ManageTableRowProps> = ({
  data,
  index,
  allowDelete,
  allowSelect,
  customRowClick,
  isFirst,
  rounded,
  inlineActionSet,
  idItemForViewModal,
  withHeaderInMobile,
  withHeaderGridTemplate,
  languageStrings,
  setLastListItem,
  rowRef,
  handleRowOnClick,
  isHrefDetailsClick,
}) => {
  const theme = useTheme();
  const { gray1 } = theme.colors;
  const {
    toggleIsChecked,
    toggleView,
    setHighlightedIndex,
    toggleMobileSelectMode,
    setLocalDeleteIndex,
    getPresentedItems,
    getGridStructure,
  } = useTableFunctions();

  const { id, isChecked } = data;

  const [{ highlightedIndex, mobileSelectMode, globalProps }] = useTableContext();
  const isExtraModal = highlightedIndex === index;
  const { mobileLimit } = globalProps;
  const [media] = useStyleContext();
  const { mediaType } = media;

  const handleClickDetails = useCallback(() => {
    if (!!customRowClick) {
      customRowClick(data);
      if (isHrefDetailsClick) {
        const href = customRowClick(data);
        return href;
      }
    } else {
      setHighlightedIndex(-1);
      toggleView(index, id);
    }
  }, [index, id, data, customRowClick, setHighlightedIndex, toggleView]);

  useEffect(() => {
    if (id && id === idItemForViewModal && checkIncludesType(inlineActionSet)) toggleView(index, id);
  }, []);

  const presentedItems = getPresentedItems(index, mediaType);
  const presentedItemsFiltered =
    mediaType.narrowMonitor && presentedItems.length > 3 ? presentedItems.slice(0, 3) : presentedItems;
  const { gridTemplate, mobileGridTemplate, mobileGridArea } = getGridStructure(index, mediaType);

  const handleSelectToggle = useCallback(
    (index: number) => {
      toggleIsChecked(index);
    },
    [toggleIsChecked],
  );

  const [onTouchStart, onTouchEnd, clearTouch] = useLongTap(
    (e: any) => setHighlightedIndex(index),
    (e: any) => handleClickDetails(),
    700,
    !mobileSelectMode && (allowDelete || allowSelect),
  );

  const switchMobileSelect = (e: any) => {
    toggleMobileSelectMode();
    handleSelectToggle(index);
    setHighlightedIndex(-1);
  };

  const clearEvents = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDelete = useCallback(() => {
    setLocalDeleteIndex(index);
  }, [index]);

  const handleSelect = useCallback(
    (e: any) => {
      handleSelectToggle(index);
      clearTouch(e);
    },
    [index, handleSelectToggle, clearTouch],
  );

  return (
    <Wrapper
      className={`${isChecked ? 'chosen' : ''} ${isExtraModal ? 'highlighted' : ''} 
      ${isFirst ? 'first' : ''} ${rounded ? 'rounded' : ''}`}
      gridTemplate={withHeaderInMobile ? withHeaderGridTemplate : gridTemplate}
      mobileGridTemplate={withHeaderInMobile ? withHeaderGridTemplate : mobileGridTemplate}
      mobileGridArea={withHeaderInMobile ? 'none' : mobileGridArea}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMove={clearTouch}
      withCheckBox={allowDelete || allowSelect}
      withHeaderInMobile={withHeaderInMobile}
      ref={rowRef}
      withHoverEffect={!!handleRowOnClick}
      onClick={handleRowOnClick ? handleRowOnClick : () => {}}
    >
      {(allowDelete || allowSelect) && (
        <GridItem
          gridArea={withHeaderInMobile ? '' : 'select'}
          mobileDisplay={mobileSelectMode}
          mobileAlignSelf={'center'}
          onTouchStart={clearEvents}
          onTouchEnd={clearEvents}
          className={'select'}
        >
          <SelectWrapper onClick={handleSelect}>
            <CheckBox
              editInProcess={false}
              toggle={isChecked}
              label=""
              handleToggle={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(e);
              }}
            />
          </SelectWrapper>
        </GridItem>
      )}
      <>
        {presentedItemsFiltered.map((item: any, columnIndex: number) => {
          const { getTextDisplay, value, component, type } = item;
          return (
            <GridItem
              key={columnIndex}
              mobileDisplay={mobileLimit === 0 || columnIndex < mobileLimit}
              gridArea={withHeaderInMobile ? '' : `item${columnIndex}`}
              className={'field'}
            >
              {type === RowItemType.CUSTOM ? (
                <>{component && component(value, TableCustomComponentState.VIEW)}</>
              ) : (
                <StyledText key={index} type={TextType.TEXT_14_GRAY} color={gray1} weight={'400'}>
                  {getTextDisplay
                    ? getTextDisplay(value)
                    : value
                    ? value.id === undefined && value.name === undefined
                      ? value
                      : !value.name
                      ? 'N/A'
                      : value.name
                    : 'N/A'}
                </StyledText>
              )}
            </GridItem>
          );
        })}
      </>
      <GridItem
        gridArea={withHeaderInMobile ? '' : 'action'}
        mobileDisplay={true}
        mobileAlignSelf={'center'}
        className={'actions'}
      >
        <ActionSection
          inlineActionSet={inlineActionSet}
          data={data}
          onDetailsClick={handleClickDetails}
          languageStrings={languageStrings}
          isHrefDetailsClick={isHrefDetailsClick}
        />
      </GridItem>
      {isExtraModal && (
        <Modal
          theme={theme}
          onTouchStart={clearEvents}
          onTouchEnd={clearEvents}
          isLast={highlightedIndex === setLastListItem}
        >
          <SelectButton onTouchStart={switchMobileSelect}>
            <TextSelect>{languageStrings.selectButtonValue}</TextSelect>
            <SelectImg />
          </SelectButton>
          <DeleteButton onTouchStart={handleDelete}>
            <DeleteText>{languageStrings.manageClientsTableHeaderDeleteButton}</DeleteText>
            <Delete />
          </DeleteButton>
        </Modal>
      )}
    </Wrapper>
  );
};
